import type { ReactNode } from "react"
import type { DunningChatMessage } from "../../types"
import { Bubble } from "../../components/DunningChatPanel"
import type { Scenario } from "../types"

// 実際の督促ケース詳細（DunningCaseDetail）と同じ見え方で検証ケースを読むための部品。
// 中身の部品（吹き出し等）は督促画面のものをそのまま使い、API や操作を持つ外枠（チャットパネル）だけを
// 読み取り専用の軽量版にしている。ヘッダーと左右の枠は CaseFrame（督促ケース詳細と共有の detailLayout）

/** 送信契機（AI が返信として書くか、当社から送る案内として書くか）。英語の値は画面に出さない */
export const ROUTING_MODE_LABELS: Record<Scenario["routingMode"], string> = {
  reactive: "お客様への返信",
  proactive: "当社から送る",
}

/** 時点の見出し（例「9月19日(土) 12:00」）。日本時間で出す */
export const asOfLabel = (iso: string) => {
  const time = Date.parse(iso)
  if (Number.isNaN(time)) return iso
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("ja-JP", {
      timeZone: "Asia/Tokyo",
      month: "numeric",
      day: "numeric",
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    })
      .formatToParts(time)
      .map((part) => [part.type, part.value])
  )
  return `${parts.month}月${parts.day}日(${parts.weekday}) ${parts.hour}:${parts.minute}`
}

/**
 * 督促チャット（LINE）パネルの読み取り専用版。吹き出しは督促チャットの Bubble をそのまま使う。
 * 中にスクロール枠は作らない（xl では左のペインごとスクロールし、見出しはペインの上端に残す）
 */
export function ChatTranscript({
  messages,
  children,
}: {
  messages: DunningChatMessage[]
  /** 会話の後ろに続けて出すもの（未送信の AIドラフトなど） */
  children?: ReactNode
}) {
  return (
    <section
      aria-label="督促チャット"
      className="flex flex-col rounded-xl border border-gray-200 bg-[#eef2f4] shadow-sm xl:shrink-0 xl:grow"
    >
      <div className="rounded-t-xl border-b border-gray-200 bg-white px-5 py-3 xl:sticky xl:top-0 xl:z-20">
        <span className="text-[1.3rem] font-bold text-gray-900">💬 督促チャット（LINE）</span>
      </div>
      <div className="flex min-h-[160px] flex-col gap-3 overflow-x-clip px-4 py-5">
        {messages.length === 0 && (
          <p className="py-8 text-center text-[1.3rem] text-black">
            会話はありません（当社からの案内）。
          </p>
        )}
        {messages.map((message) => (
          <Bubble key={message.messageId} message={message} />
        ))}
        {children}
      </div>
    </section>
  )
}
